import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Truck, Package, MapPin, Calculator, FileDown, MapPinned, XCircle, ArrowLeft, AlertTriangle, Info } from 'lucide-react';

const CARRIERS = [
    { value: 'dhl', label: 'DHL Express', logo: '🔴', desc: 'Cobertura nacional e internacional, entrega exprés' },
    { value: 'fedex', label: 'FedEx', logo: '🟣', desc: 'Red global, opciones de entrega al día siguiente' },
    { value: 'estafeta', label: 'Estafeta', logo: '🔵', desc: 'Cobertura nacional amplia, servicio terrestre y aéreo' },
    { value: 'redpack', label: 'Redpack', logo: '🔴', desc: 'Enfocado en entregas locales y foráneas en México' },
    { value: 'paquetexpress', label: 'Paquetexpress', logo: '🟤', desc: 'Económico, ideal para paquetes de bajo peso' },
    { value: 'sendex', label: 'Sendex', logo: '🟢', desc: 'Especializado en envíos masivos y ecommerce' },
    { value: 'ups', label: 'UPS', logo: '🟤', desc: 'Cobertura internacional, rastreo en tiempo real' },
    { value: 'ampm', label: 'AMPM', logo: '⚫', desc: 'Entregas exprés CDMX y área metropolitana' },
    { value: 'noventa9minutos', label: '99 Minutos', logo: '🟡', desc: 'Última milla y entregas mismo día en ciudades principales' },
    { value: 'quiken', label: 'Quiken', logo: '🟠', desc: 'Mensajería urbana de alta velocidad' },
];

// Reference rates table (MXN, approximate, updated periodically)
const RATES_TABLE = [
    { carrier: 'DHL Express',     kg1: 119, kg2: 155, kg5: 220, kg10: 380, kg20: 620, cobertura: 'Nacional + Internacional', entrega: '1-2 días' },
    { carrier: 'FedEx',           kg1: 105, kg2: 148, kg5: 210, kg10: 365, kg20: 590, cobertura: 'Nacional + Internacional', entrega: '1-3 días' },
    { carrier: 'Estafeta',        kg1:  89, kg2: 120, kg5: 178, kg10: 310, kg20: 510, cobertura: 'Nacional',                 entrega: '2-4 días' },
    { carrier: 'Redpack',         kg1:  82, kg2: 112, kg5: 165, kg10: 290, kg20: 480, cobertura: 'Nacional',                 entrega: '2-4 días' },
    { carrier: 'Paquetexpress',   kg1:  75, kg2: 100, kg5: 150, kg10: 270, kg20: 440, cobertura: 'Nacional',                 entrega: '3-5 días' },
    { carrier: 'Sendex',          kg1:  80, kg2: 108, kg5: 158, kg10: 280, kg20: 455, cobertura: 'Nacional',                 entrega: '2-4 días' },
    { carrier: 'UPS',             kg1: 110, kg2: 145, kg5: 200, kg10: 355, kg20: 570, cobertura: 'Nacional + Internacional', entrega: '1-3 días' },
    { carrier: 'AMPM',            kg1:  65, kg2:  90, kg5: 130, kg10: 240, kg20: 400, cobertura: 'CDMX y ZM',               entrega: 'Mismo día' },
    { carrier: '99 Minutos',      kg1:  60, kg2:  85, kg5: 120, kg10: 220, kg20: 370, cobertura: 'Ciudades principales',     entrega: 'Mismo día' },
    { carrier: 'Quiken',          kg1:  55, kg2:  80, kg5: 115, kg10: 210, kg20: 350, cobertura: 'CDMX y ZM',               entrega: 'Mismo día' },
];

const emptyAddress = { name: '', phone: '', street: '', city: '', state: '', country: 'MX', postalCode: '' };
const emptyPackage = { content: 'Producto', declaredValue: 500, weight: 1, dimensions: { length: 20, width: 15, height: 10 } };

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DashboardEnvios() {
    const nav = useNavigate();
    const [tab, setTab] = useState('quote');
    const [status, setStatus] = useState(null);
    const [origin, setOrigin] = useState({ ...emptyAddress });
    const [destination, setDestination] = useState({ ...emptyAddress });
    const [pkg, setPkg] = useState({ ...emptyPackage });
    const [carrier, setCarrier] = useState('dhl');
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [shipments, setShipments] = useState([]);
    const [trackNumber, setTrackNumber] = useState('');
    const [trackResult, setTrackResult] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        api.get('/envios/status').then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false }));
        api.get('/me/envios').then(({ data }) => setShipments(data || [])).catch(() => {});
    }, []);

    const loadShipments = () => api.get('/me/envios').then(({ data }) => setShipments(data || []));

    const doQuote = async () => {
        setLoading(true);
        setQuotes([]);
        try {
            const { data } = await api.post('/envios/quote', { origin, destination, packages: [pkg], carrier });
            const items = Array.isArray(data?.data) ? data.data : [];
            if (items.length === 0) {
                toast.info('El transportista no devolvió tarifas para esta ruta.');
            }
            setQuotes(items);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cotizar');
        } finally { setLoading(false); }
    };

    const generateLabel = async (quote) => {
        setGenerating(true);
        try {
            const { data } = await api.post('/envios/labels', {
                origin, destination, packages: [pkg], carrier, service: quote.service,
            });
            toast.success(`Guía generada · ${data.trackingNumber || 's/n'}`);
            setTab('shipments');
            loadShipments();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al generar guía');
        } finally { setGenerating(false); }
    };

    const doTrack = async (tn) => {
        const num = tn || trackNumber;
        if (!num) return;
        try {
            const { data } = await api.get(`/envios/track/${encodeURIComponent(num)}`);
            setTrackResult(data);
            setTab('track');
            setTrackNumber(num);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al rastrear'); }
    };

    const cancelShipment = async (s) => {
        if (!window.confirm(`¿Cancelar guía ${s.trackingNumber}?`)) return;
        try {
            await api.post('/envios/cancel', { carrier: s.carrier, trackingNumber: s.trackingNumber });
            toast.success('Guía cancelada');
            loadShipments();
        } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo cancelar'); }
    };

    const AddressForm = ({ title, value, onChange, icon: Icon, testPrefix }) => (
        <Card>
            <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Icon className="h-4 w-4" />{title}</div>
                <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Nombre</Label><Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid={`${testPrefix}-name`} /></div>
                    <div><Label className="text-xs">Teléfono</Label><Input value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })} placeholder="+525511111111" data-testid={`${testPrefix}-phone`} /></div>
                    <div className="col-span-2"><Label className="text-xs">Calle y número</Label><Input value={value.street} onChange={(e) => onChange({ ...value, street: e.target.value })} data-testid={`${testPrefix}-street`} /></div>
                    <div><Label className="text-xs">Ciudad</Label><Input value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} data-testid={`${testPrefix}-city`} /></div>
                    <div><Label className="text-xs">Estado (código)</Label><Input value={value.state} onChange={(e) => onChange({ ...value, state: e.target.value })} placeholder="NL" data-testid={`${testPrefix}-state`} /></div>
                    <div><Label className="text-xs">Código Postal</Label><Input value={value.postalCode} onChange={(e) => onChange({ ...value, postalCode: e.target.value })} data-testid={`${testPrefix}-zip`} /></div>
                    <div><Label className="text-xs">País</Label><Input value={value.country} onChange={(e) => onChange({ ...value, country: e.target.value.toUpperCase() })} maxLength={2} /></div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-4" data-testid="dashboard-envios">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="h-4 w-4 mr-1" />Volver</Button>
                    <Truck className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">OkEnvíos</h1>
                    {status?.configured ? (
                        <Badge className="bg-emerald-100 text-emerald-800 ml-2">Activo · {status.mode}</Badge>
                    ) : (
                        <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700">Sin configurar</Badge>
                    )}
                </div>
            </div>

            {!status?.configured && (
                <Alert className="border-amber-200 bg-amber-50" data-testid="envios-not-configured">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>OkEnvíos aún no está configurado. Contacta al superadmin.</AlertDescription>
                </Alert>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="quote" data-testid="envios-tab-quote"><Calculator className="h-3.5 w-3.5 mr-1" />Cotizar</TabsTrigger>
                    <TabsTrigger value="shipments" data-testid="envios-tab-shipments"><Package className="h-3.5 w-3.5 mr-1" />Mis guías ({shipments.length})</TabsTrigger>
                    <TabsTrigger value="track" data-testid="envios-tab-track"><MapPinned className="h-3.5 w-3.5 mr-1" />Rastrear</TabsTrigger>
                    <TabsTrigger value="rates" data-testid="envios-tab-rates"><Info className="h-3.5 w-3.5 mr-1" />Tarifas</TabsTrigger>
                </TabsList>

                <TabsContent value="quote" className="space-y-4 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <AddressForm title="Origen (vendedor)" value={origin} onChange={setOrigin} icon={MapPin} testPrefix="origin" />
                        <AddressForm title="Destino (comprador)" value={destination} onChange={setDestination} icon={MapPin} testPrefix="destination" />
                    </div>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Package className="h-4 w-4" />Paquete</div>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                <div className="col-span-2"><Label className="text-xs">Contenido</Label><Input value={pkg.content} onChange={(e) => setPkg({ ...pkg, content: e.target.value })} /></div>
                                <div><Label className="text-xs">Valor $</Label><Input type="number" value={pkg.declaredValue} onChange={(e) => setPkg({ ...pkg, declaredValue: parseFloat(e.target.value) || 0 })} data-testid="pkg-value" /></div>
                                <div><Label className="text-xs">Peso kg</Label><Input type="number" step="0.1" value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: parseFloat(e.target.value) || 0 })} data-testid="pkg-weight" /></div>
                                <div><Label className="text-xs">Largo cm</Label><Input type="number" value={pkg.dimensions.length} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, length: parseFloat(e.target.value) || 0 } })} /></div>
                                <div><Label className="text-xs">Ancho cm</Label><Input type="number" value={pkg.dimensions.width} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, width: parseFloat(e.target.value) || 0 } })} /></div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                <div><Label className="text-xs">Alto cm</Label><Input type="number" value={pkg.dimensions.height} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, height: parseFloat(e.target.value) || 0 } })} /></div>
                                <div className="md:col-span-2">
                                    <Label className="text-xs">Paquetería</Label>
                                    <Select value={carrier} onValueChange={setCarrier}>
                                        <SelectTrigger data-testid="envios-carrier"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CARRIERS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-end">
                                    <Button className="w-full" onClick={doQuote} disabled={loading || !origin.postalCode || !destination.postalCode} data-testid="envios-quote-btn">
                                        <Calculator className="h-4 w-4 mr-1" />{loading ? 'Cotizando…' : 'Cotizar envío'}
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {loading && <div className="grid gap-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>}

                    {quotes.length > 0 && (
                        <div className="space-y-2" data-testid="envios-quotes-list">
                            <h3 className="text-sm font-semibold">Servicios disponibles ({quotes.length})</h3>
                            {quotes.map((q, idx) => (
                                <Card key={idx} data-testid={`envios-quote-${idx}`}>
                                    <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                                        <div>
                                            <div className="font-semibold">{q.carrierDescription || q.carrier} · {q.serviceDescription || q.service}</div>
                                            <div className="text-xs text-muted-foreground">Entrega estimada: {q.deliveryEstimate || 's/n'}</div>
                                            {q.zone && <div className="text-xs">Zona {q.zone}</div>}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold">${fmt(q.totalPrice)} <span className="text-xs font-normal text-muted-foreground">{q.currency || 'MXN'}</span></div>
                                            <Button size="sm" className="mt-1" onClick={() => generateLabel(q)} disabled={generating} data-testid={`envios-generate-${idx}`}>
                                                <FileDown className="h-4 w-4 mr-1" />Generar guía
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="shipments" className="pt-4">
                    {shipments.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-10 flex flex-col items-center gap-2">
                            <Package className="h-8 w-8 text-slate-400" />
                            Aún no has generado guías. Ve a la pestaña Cotizar.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {shipments.map(s => (
                                <Card key={s.id} data-testid={`shipment-${s.trackingNumber}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="font-mono font-bold">{s.trackingNumber || 'sin tracking'}</div>
                                                <div className="text-xs text-muted-foreground uppercase">{s.carrier} · {s.service}</div>
                                            </div>
                                            {s.status === 'cancelled' ? <Badge variant="outline" className="border-destructive text-destructive">Cancelada</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Activa</Badge>}
                                        </div>
                                        <div className="text-xs mt-2">
                                            <div>Origen CP: <span className="font-mono">{s.origin_zip}</span></div>
                                            <div>Destino CP: <span className="font-mono">{s.destination_zip}</span></div>
                                            {s.totalPrice && <div>Costo: <span className="font-semibold">${fmt(s.totalPrice)} {s.currency}</span></div>}
                                        </div>
                                        <div className="flex gap-2 mt-3 flex-wrap">
                                            {s.labelUrl && <Button size="sm" variant="outline" asChild><a href={s.labelUrl} target="_blank" rel="noreferrer"><FileDown className="h-3.5 w-3.5 mr-1" />Guía PDF</a></Button>}
                                            {s.trackingNumber && <Button size="sm" variant="outline" onClick={() => doTrack(s.trackingNumber)}><MapPinned className="h-3.5 w-3.5 mr-1" />Rastrear</Button>}
                                            {s.status !== 'cancelled' && s.trackingNumber && <Button size="sm" variant="ghost" onClick={() => cancelShipment(s)}><XCircle className="h-3.5 w-3.5 mr-1 text-destructive" />Cancelar</Button>}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="track" className="pt-4 space-y-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex gap-2">
                                <Input placeholder="Número de rastreo" value={trackNumber} onChange={(e) => setTrackNumber(e.target.value)} data-testid="envios-track-input" />
                                <Button onClick={() => doTrack()} data-testid="envios-track-btn"><MapPinned className="h-4 w-4 mr-1" />Buscar</Button>
                            </div>
                        </CardContent>
                    </Card>
                    {trackResult && (
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2"><Info className="h-4 w-4" /><span className="font-semibold text-sm">Resultado de rastreo</span></div>
                                <pre className="text-xs bg-slate-50 p-3 rounded max-h-96 overflow-auto">{JSON.stringify(trackResult, null, 2)}</pre>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Rates reference table */}
                <TabsContent value="rates" className="pt-4 space-y-5">
                    <div>
                        <h2 className="text-base font-semibold mb-1">Paqueterías disponibles en OkEnvíos</h2>
                        <p className="text-xs text-muted-foreground">Estas paqueterías están integradas a través de envia.com. Usa la pestaña Cotizar para obtener precios exactos en tiempo real.</p>
                    </div>

                    {/* Carrier cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {CARRIERS.map(c => (
                            <div key={c.value} className="rounded-xl border bg-card p-3 flex items-start gap-3">
                                <div className="text-2xl mt-0.5">{c.logo}</div>
                                <div>
                                    <div className="font-semibold text-sm">{c.label}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{c.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Rates table */}
                    <div>
                        <h2 className="text-base font-semibold mb-1">Tarifas de referencia (MXN, aprox.)</h2>
                        <p className="text-xs text-muted-foreground mb-3">Precios estimados para envíos nacionales. Las tarifas reales varían según origen, destino, peso volumétrico y vigencia del contrato. Usa Cotizar para el precio exacto.</p>
                        <div className="rounded-xl border bg-card overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[11px] text-muted-foreground bg-secondary/50 text-left">
                                    <tr>
                                        <th className="px-3 py-2">Paquetería</th>
                                        <th className="px-3 py-2 text-right">1 kg</th>
                                        <th className="px-3 py-2 text-right">2 kg</th>
                                        <th className="px-3 py-2 text-right">5 kg</th>
                                        <th className="px-3 py-2 text-right">10 kg</th>
                                        <th className="px-3 py-2 text-right">20 kg</th>
                                        <th className="px-3 py-2">Cobertura</th>
                                        <th className="px-3 py-2">Entrega</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {RATES_TABLE.map((r, i) => (
                                        <tr key={i} className={`border-t ${i % 2 === 0 ? '' : 'bg-secondary/20'}`}>
                                            <td className="px-3 py-2 font-medium">{r.carrier}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">${r.kg1}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">${r.kg2}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">${r.kg5}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">${r.kg10}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">${r.kg20}</td>
                                            <td className="px-3 py-2 text-[10px] text-muted-foreground">{r.cobertura}</td>
                                            <td className="px-3 py-2 text-[10px]">{r.entrega}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">* Tarifas orientativas de referencia. El precio final se calcula en tiempo real al cotizar. Sujeto a cambios sin previo aviso.</p>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
